/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Analytics › People › Internal people (contract section 2 "Audience", section 5): everyone marked internal, with why
 * ("Marked by IT", "From group QA"), one-tap import of each directory group the audience read offers
 * (POST /v1/admin/usage/audience/import), add by account and remove (PUT /v1/admin/usage/audience); and the screens and
 * actions the service hides under "Admin screens hidden". Admins change the list; auditors read it.
 */
import { useState } from 'react';
import { Download, UserMinus, UserPlus, Users } from 'lucide-react';
import { api } from '../../lib/api';
import { importOutcomeText, normalizeImportOutcome, personLabel, sourceLabel, typedAccounts, type AudiencePerson, type AudienceView } from '../../lib/usage';
import { Button, Callout, EmptyState, Input, Panel, Table, Tag, type Column } from '../../ui';
import { count, dateTime } from '../../ui/format';
import { UsageError, usageFailure } from './parts';

export interface AudiencePanelProps {
  view: AudienceView | null;
  state: 'loading' | 'ready' | 'failed';
  error: unknown;
  canEdit: boolean;
  names: ReadonlyMap<string, string>;
  onRetry: () => void;
  /** After a write succeeded: the caller reads the audience and the people again. */
  onChanged: () => void;
}

export function AudiencePanel({ view, state, error, canEdit, names, onRetry, onChanged }: AudiencePanelProps) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null);

  const run = async (what: string, action: () => Promise<unknown>, done: string | ((answer: unknown) => string)) => {
    setBusy(what);
    setNote(null);
    try {
      const answer = await action();
      setNote({ tone: 'ok', text: typeof done === 'string' ? done : done(answer) });
      onChanged();
    } catch (failure: unknown) {
      setNote({ tone: 'bad', text: usageFailure(failure, `${what} did not complete.`) });
    } finally {
      setBusy(null);
    }
  };

  const add = () => {
    const { upns, rejected } = typedAccounts(typed);
    if (!upns.length) {
      setNote({ tone: 'bad', text: rejected.length ? `Not an account: ${rejected.join(', ')}. Type a full e-mail address.` : 'Type an account to add.' });
      return;
    }
    void run('add', async () => {
      await api.setUsageAudience(upns, true);
      setTyped('');
    }, `${count(upns.length)} ${upns.length === 1 ? 'person' : 'people'} marked internal${rejected.length ? `; not an account: ${rejected.join(', ')}` : ''}.`);
  };

  const columns: Array<Column<AudiencePerson>> = [
    {
      key: 'person', header: 'Person',
      cell: (row) => {
        const name = personLabel(row.upn, names);
        return <span className="ui-cell-stack"><span>{name}</span>{name !== row.upn ? <span className="ui-cell-sub is-mono">{row.upn}</span> : null}</span>;
      },
    },
    { key: 'source', header: 'Why', cell: (row) => sourceLabel(row.source, view?.groups ?? []) },
    { key: 'set', header: 'Since', hideOnPhone: true, muted: true, cell: (row) => (row.setAt ? `${dateTime(row.setAt)}${row.setBy ? ` by ${row.setBy}` : ''}` : row.setBy ? `by ${row.setBy}` : '—') },
    ...(canEdit ? [{
      key: 'remove', header: '', align: 'end' as const, width: '112px',
      cell: (row: AudiencePerson) => <Button variant="ghost" size="sm" icon={UserMinus} busy={busy === `remove:${row.upn}`} onClick={() => void run(`remove:${row.upn}`, () => api.setUsageAudience([row.upn], false), `${row.upn} is no longer marked internal.`)}>Remove</Button>,
    }] : []),
  ];

  return (
    <Panel
      flush
      title="Internal people"
      description="Left out of General users: the dev team, QA and IT admins by their directory groups, and anyone marked by account. A mark applies to every window at once, past days included."
      footer={view && view.hidden.length ? (
        <span className="ui-foot-text">
          <span>{`Hidden under “Admin screens hidden” (${count(view.hidden.length)}): absent from rankings, journeys, realtime screens and top screens; sessions and people still count.`}</span>
          <span className="ui-chips">{view.hidden.map((name) => <Tag key={name}>{name}</Tag>)}</span>
        </span>
      ) : undefined}
    >
      {state === 'failed' ? <div className="ui-panel-pad"><UsageError title="The internal people could not be loaded" error={error} onRetry={onRetry} /></div> : (
        <>
          {canEdit ? (
            <div className="ui-panel-pad ui-panel-pad--divided ui-stack">
              {note ? <Callout tone={note.tone} role={note.tone === 'bad' ? 'alert' : 'status'}>{note.text}</Callout> : null}
              <div className="ui-actions" aria-label="Import a group">
                {(view?.groups ?? []).map((group) => (
                  <Button key={group.id} size="sm" icon={Download} busy={busy === `import:${group.id}`} disabled={state !== 'ready'} onClick={() => void run(`import:${group.id}`, () => api.importUsageAudience(group.id), (answer) => importOutcomeText(group.label, normalizeImportOutcome(answer)))}>
                    Import {group.label}
                  </Button>
                ))}
                {state === 'ready' && !(view?.groups.length) ? <span className="ui-cell-sub">The service offers no directory groups to import.</span> : null}
              </div>
              <div className="ui-actions">
                <Input size="sm" className="ui-grow" aria-label="Add by account" placeholder="name@exante360.com, one or more" value={typed} onChange={(event) => setTyped(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); add(); } }} />
                <Button size="sm" icon={UserPlus} busy={busy === 'add'} disabled={state !== 'ready'} onClick={add}>Add</Button>
              </div>
            </div>
          ) : (
            <div className="ui-panel-pad ui-panel-pad--divided"><Callout tone="neutral">Read-only: auditors see who is marked internal; only IT admins change the list.</Callout></div>
          )}
          <Table
            label="Internal people"
            columns={columns}
            rows={view?.people ?? []}
            rowKey={(row) => row.upn}
            loading={state === 'loading'}
            skeletonRows={3}
            stickyHeader={false}
            empty={<EmptyState compact icon={Users} title="Nobody is marked internal yet">{canEdit ? 'Import the three groups above before anyone reads the numbers, or add people by account.' : 'An IT admin imports the groups or adds people by account.'}</EmptyState>}
          />
        </>
      )}
    </Panel>
  );
}
